import { Sparkline } from './Sparkline';
import type { SyncDashboardEntry } from '../../types/admin';

interface Props {
  entry: SyncDashboardEntry;
  onTrigger?: (syncName: string) => void;
  triggerPending: boolean;
}

const statusBadge: Record<string, string> = {
  SUCCESS:  'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  FAILED:   'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  QUEUED:   'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  RUNNING:  'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 animate-pulse',
  SKIPPED:  'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  UNKNOWN:  'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
};

export function SyncDashboardCard({ entry, onTrigger, triggerPending }: Props) {
  const status = entry.lastRun?.status ?? 'UNKNOWN';
  const counts = entry.lastRun
    ? entry.sparkline[entry.sparkline.length - 1]?.rowCounts
    : null;
  const durations = entry.sparkline.map((p) => p.durationMs / 1000);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{entry.direction}</div>
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{entry.syncName}</div>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded ${statusBadge[status] ?? statusBadge.UNKNOWN}`}>
          {status}
        </span>
      </div>

      {counts && (
        <div className="text-xs text-gray-600 dark:text-gray-400">
          Created {counts.created} · Updated {counts.updated} · Errors {counts.errors}
        </div>
      )}

      <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
        <Sparkline values={durations} />
        <span className="text-xs text-gray-500 dark:text-gray-400">last 7 (s)</span>
      </div>

      <div className="text-xs text-gray-500 dark:text-gray-400">
        {entry.schedule?.label ?? 'Manual only'}
        {entry.nextRunAt && (
          <span> · next: {new Date(entry.nextRunAt).toLocaleString()}</span>
        )}
      </div>

      {onTrigger && (
        <button
          disabled={triggerPending}
          onClick={() => onTrigger(entry.syncName)}
          className="self-start text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          {triggerPending ? 'Triggering…' : 'Trigger now'}
        </button>
      )}
    </div>
  );
}
